'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import type { CommentsPage } from '@/lib/api/comments';
import { ActivitySection } from '@/lib/components/ActivitySection';
import { TopHolders } from '@/lib/components/TopHolders';
import { MarketItem } from '@/lib/types/markets/market';
import { Tabs } from '@/lib/ui-kit/vercel-tabs';
import { EventComments } from '../Comments/EventComments';

interface EventCommentsTabsProps {
  marketId: string;
  outcomes: MarketItem[];
  initialComments?: CommentsPage;
}

export function EventCommentsTabs({ marketId, outcomes, initialComments }: EventCommentsTabsProps) {
  const t = useTranslations('event.tabs');
  const [activeTab, setActiveTab] = useState('comments');

  const TABS = useMemo(
    () => [
      { id: 'comments', label: t('comments') },
      { id: 'topHolders', label: t('topHolders') },
      { id: 'activity', label: t('activity') },
    ],
    [t],
  );

  return (
    <div>
      <Tabs
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="label-lg mb-4"
        hideBottomBorder
      />

      <AnimatePresence mode="wait">
        {activeTab === 'comments' && (
          <motion.div
            key="comments"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="flex min-h-[350px] flex-col gap-4"
          >
            <EventComments eventId={marketId} initialPage={initialComments} />
          </motion.div>
        )}

        {activeTab === 'topHolders' && (
          <motion.div
            key="topHolders"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="flex min-h-[350px] flex-col gap-4"
          >
            <TopHolders outcomes={outcomes} />
          </motion.div>
        )}

        {activeTab === 'activity' && (
          <motion.div
            key="activity"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="flex min-h-[350px] flex-col gap-4"
          >
            <ActivitySection eventId={marketId} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
